import { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../auth/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { useT } from '../i18n/i18n';
import { useLearner } from '../learners/LearnerContext';
import { AREA_LABEL, OUTCOME_LABEL, fetchReview, signReview, updateReview, type Review, type ReviewOutcome, type ReviewTarget } from './api';
import { useDates } from './dates';
import Evidence from './Evidence';
import { useStamp } from './reviewStamp';
import './reviews.css';

type T = ReturnType<typeof useT>;
type Group = { goal: string; area: ReviewTarget['area']; targets: ReviewTarget[] };

function groups(targets: ReviewTarget[]): Group[] {
  const out: Group[] = [];
  for (const x of targets) {
    const g = out.find((o) => o.goal === x.goal);
    if (g) g.targets.push(x);
    else out.push({ goal: x.goal, area: x.area, targets: [x] });
  }
  return out;
}

function readableDates(s: string): string {
  const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return s.replace(/((d{4})-(d{2})-(d{2}))/g, (_m, y: string, mo: string, d: string) => `(${fmt.format(new Date(Number(y), Number(mo) - 1, Number(d)))})`);
}

function autoEvidence(x: ReviewTarget, t: T): string[] {
  const lines = [t('{done} of {planned} sessions done, {missed} missed', { done: x.done, planned: x.planned, missed: x.missed })];
  lines.push(x.results === 1 ? t('1 result recorded') : t('{results} results recorded', { results: x.results }));
  if (x.firstResult) lines.push(t('First: {first}', { first: readableDates(x.firstResult) }));
  if (x.lastResult) lines.push(t('Latest: {last}', { last: readableDates(x.lastResult) }));
  return lines;
}

const blank = (s: string | null) => (s && s.trim() ? s.trim() : null);

export default function ReviewEditor({ learnerId, reviewId, consultant, onBack }: { learnerId: string; reviewId: string; consultant: boolean; onBack: () => void }) {
  const t = useT();
  const d = useDates();
  const stamp = useStamp();
  const id = useId();
  const { user } = useAuth();
  const { learner } = useLearner();
  const [review, setReview] = useState<Review | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [name, setName] = useState(user?.displayName ?? '');
  const [confirmSign, setConfirmSign] = useState(false);
  const [printing, setPrinting] = useState(false);

  const load = useCallback(async () => {
    try {
      setReview(await fetchReview(learnerId, reviewId));
      setDirty(false);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('The review could not be loaded just now.'));
    }
  }, [learnerId, reviewId, t]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!printing) return;
    const done = () => {
      document.body.classList.remove('planner-printing');
      setPrinting(false);
    };
    document.body.classList.add('planner-printing');
    window.addEventListener('afterprint', done, { once: true });
    window.print();
    return () => {
      window.removeEventListener('afterprint', done);
      document.body.classList.remove('planner-printing');
    };
  }, [printing]);

  if (!review) {
    return (
      <div className="planner-review-board">
        <div className="toolbar"><button type="button" className="btn ghost" onClick={onBack}>{t('← Back to reviews')}</button></div>
        {error ? <div className="error" role="alert">{error}</div> : <p className="muted">{t('Loading…')}</p>}
      </div>
    );
  }

  const r = review;
  const signed = r.status === 'SIGNED';

  function edit(patch: Partial<Review>) {
    setReview((o) => (o ? { ...o, ...patch } : o));
    setDirty(true);
    setSaved(false);
  }

  function editTarget(targetId: string, patch: Partial<ReviewTarget>) {
    setReview((o) => (o ? { ...o, targets: o.targets.map((x) => (x.targetId === targetId ? { ...x, ...patch } : x)) } : o));
    setDirty(true);
    setSaved(false);
  }

  async function save(): Promise<boolean> {
    if (!r.title.trim()) { setError(t('Give the review a title.')); return false; }
    await updateReview(learnerId, r.id, {
      title: r.title.trim(),
      attendees: blank(r.attendees),
      parentViews: blank(r.parentViews),
      learnerViews: blank(r.learnerViews),
      summary: blank(r.summary),
      targets: r.targets.map((x) => ({ targetId: x.targetId, outcome: x.outcome, evidence: blank(x.evidence), barriers: blank(x.barriers), provisionOk: x.provisionOk, nextSteps: blank(x.nextSteps) })),
    });
    return true;
  }

  async function saveDraft() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      if (await save()) {
        await load();
        setSaved(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  async function sign() {
    setConfirmSign(false);
    if (!name.trim()) { setError(t('Type your name to sign.')); return; }
    setBusy(true);
    setError(null);
    try {
      if (!signed && dirty && !(await save())) return;
      await signReview(learnerId, r.id, name.trim());
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('The review could not be signed just now.'));
    } finally {
      setBusy(false);
    }
  }

  const canParentSign = !consultant && !signed;
  const canConsultantSign = consultant && !r.signedConsultantAt;

  return (
    <div className="planner-review-board">
      <div className="toolbar planner-review-bar">
        <button type="button" className="btn ghost" onClick={onBack}>{t('← Back to reviews')}</button>
        <button type="button" className="btn secondary" onClick={() => setPrinting(true)}>{t('Print or save as PDF')}</button>
      </div>

      <article className="card planner-review-sheet">
        <header className="planner-review-head">
          {signed ? <h3 className="planner-review-sheet-title">{r.title}</h3> : (
            <div className="field">
              <label htmlFor={`${id}-title`}>{t('Title')}</label>
              <input id={`${id}-title`} maxLength={200} value={r.title} onChange={(e) => edit({ title: e.target.value })} />
            </div>
          )}
          <p className="planner-review-period">{t('From {from} to {to}', { from: d.long(r.from), to: d.long(r.to) })}</p>
          <span className={`pill planner-review-status is-${r.status.toLowerCase()}`}>{signed ? t('Signed') : t('Draft')}</span>
        </header>

        <section className="planner-review-section" aria-labelledby={`${id}-views`}>
          <h4 id={`${id}-views`}>{t('Views and who took part')}</h4>
          {signed ? (
            <dl className="planner-review-read">
              <ReadRow label={t('Who took part')} value={r.attendees} />
              <ReadRow label={t("Parent's views")} value={r.parentViews} />
              <ReadRow label={t("Learner's views")} value={r.learnerViews} />
              <ReadRow label={t('Overall summary')} value={r.summary} />
            </dl>
          ) : (
            <>
              <div className="field">
                <label htmlFor={`${id}-att`}>{t('Who took part')}</label>
                <textarea id={`${id}-att`} rows={2} maxLength={2000} value={r.attendees ?? ''} onChange={(e) => edit({ attendees: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor={`${id}-pv`}>{t("Parent's views")}</label>
                <textarea id={`${id}-pv`} rows={4} maxLength={4000} value={r.parentViews ?? ''} onChange={(e) => edit({ parentViews: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor={`${id}-lv`}>{t("Learner's views")}</label>
                <textarea id={`${id}-lv`} rows={4} maxLength={4000} value={r.learnerViews ?? ''} onChange={(e) => edit({ learnerViews: e.target.value })} aria-describedby={`${id}-lv-hint`} />
                <span className="field-hint" id={`${id}-lv-hint`}>{t('In their own words or how they showed it, e.g. pictures they chose.')}</span>
              </div>
              <div className="field">
                <label htmlFor={`${id}-sum`}>{t('Overall summary')}</label>
                <textarea id={`${id}-sum`} rows={4} maxLength={4000} value={r.summary ?? ''} onChange={(e) => edit({ summary: e.target.value })} />
              </div>
            </>
          )}
        </section>

        {r.targets.length === 0 && <p className="muted">{t('No targets were active in this period.')}</p>}
        {groups(r.targets).map((g) => (
          <section key={g.goal} className="planner-review-goal">
            <p className="planner-review-area">{t(AREA_LABEL[g.area])}</p>
            <h4 className="planner-review-goal-title">{g.goal}</h4>
            <ol className="planner-review-targets">
              {g.targets.map((x) => (
                <li key={x.targetId} className="planner-review-target">
                  <TargetBlock x={x} signed={signed} onChange={(p) => editTarget(x.targetId, p)} />
                </li>
              ))}
            </ol>
          </section>
        ))}

        <section className="planner-review-section">
          <Evidence learnerId={learnerId} subjectType="REVIEW" subjectId={r.id} readOnly={signed} />
        </section>

        {error && <div className="error" role="alert">{error}</div>}
        {!signed && (
          <div className="toolbar planner-review-save">
            <button type="button" className="btn" disabled={busy || !dirty} onClick={() => void saveDraft()}>{t('Save draft')}</button>
            <span className="muted" aria-live="polite">{saved ? t('Saved.') : dirty ? t('Changes not saved yet.') : ''}</span>
          </div>
        )}

        <section className="planner-review-section planner-review-sign" aria-labelledby={`${id}-sign`}>
          <h4 id={`${id}-sign`}>{t('Signatures')}</h4>
          {r.signedParentName && r.signedParentAt && (
            <p className="planner-review-signature">{t('Signed by {name} on {date}', { name: r.signedParentName, date: stamp(r.signedParentAt) })}</p>
          )}
          {r.signedConsultantName && r.signedConsultantAt && (
            <p className="planner-review-signature">{t('Signed by {name}, consultant, on {date}', { name: r.signedConsultantName, date: stamp(r.signedConsultantAt) })}</p>
          )}
          {!signed && !r.signedConsultantAt && <p className="muted">{t('Not signed yet.')}</p>}
          {canParentSign && (
            <p className="field-hint">{t('Signing freezes the review. After that nobody can change or delete it, so it stays a fixed record of what was agreed.')}</p>
          )}
          {canConsultantSign && (
            <p className="field-hint">{signed
              ? t('Add your signature to show you agree with this review.')
              : t('Add your signature to show you agree with this review. If it is changed before the parent signs, your signature is removed and you can sign again.')}
            </p>
          )}
          {(canParentSign || canConsultantSign) && (
            <div className="planner-review-sign-form">
              <div className="field">
                <label htmlFor={`${id}-name`}>{t('Your name')}</label>
                <input id={`${id}-name`} maxLength={120} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              </div>
              <div className="toolbar">
                {canParentSign && <button type="button" className="btn" disabled={busy || !name.trim()} onClick={() => setConfirmSign(true)}>{t('Sign the review')}</button>}
                {canConsultantSign && <button type="button" className="btn" disabled={busy || !name.trim()} onClick={() => void sign()}>{t('Sign as consultant')}</button>}
              </div>
            </div>
          )}
        </section>
      </article>

      <ConfirmDialog
        open={confirmSign}
        title={t('Sign and freeze this review?')}
        message={t('Any changes not yet saved are saved first. Once signed, the review cannot be changed or deleted.')}
        confirmLabel={t('Sign the review')}
        onConfirm={() => void sign()}
        onCancel={() => setConfirmSign(false)}
      />
      {printing && createPortal(<PrintReview review={r} learnerName={learner?.name ?? null} />, document.body)}
    </div>
  );
}

function ReadRow({ label, value }: { label: string; value: string | null }) {
  const t = useT();
  return <div><dt>{label}</dt><dd>{value ?? <span className="muted">{t('Not recorded')}</span>}</dd></div>;
}

function TargetBlock({ x, signed, onChange }: { x: ReviewTarget; signed: boolean; onChange: (p: Partial<ReviewTarget>) => void }) {
  const t = useT();
  const id = useId();
  const auto = autoEvidence(x, t);
  return (
    <div className="planner-review-target-body">
      <h5 className="planner-review-target-title">{x.statement}</h5>
      <dl className="planner-goal-meta">
        {x.baseline && <div><dt>{t('Baseline')}</dt><dd>{x.baseline}</dd></div>}
        {x.successCriterion && <div><dt>{t('How we will know')}</dt><dd>{x.successCriterion}</dd></div>}
      </dl>
      <ul className="planner-review-auto" aria-label={t('From the sessions and results')}>
        {auto.map((line) => <li key={line}>{line}</li>)}
      </ul>

      {signed ? (
        <dl className="planner-review-read">
          <ReadRow label={t('Outcome')} value={x.outcome ? t(OUTCOME_LABEL[x.outcome]) : null} />
          <ReadRow label={t('Evidence')} value={x.evidence} />
          <ReadRow label={t('What got in the way')} value={x.barriers} />
          <ReadRow label={t('Is the support still right?')} value={x.provisionOk == null ? null : x.provisionOk ? t('Yes') : t('No')} />
          <ReadRow label={t('Next steps')} value={x.nextSteps} />
        </dl>
      ) : (
        <>
          <fieldset className="planner-fieldset">
            <legend>{t('Outcome')}</legend>
            <div className="planner-radios">
              {(Object.keys(OUTCOME_LABEL) as ReviewOutcome[]).map((o) => (
                <label key={o}>
                  <input type="radio" name={`${id}-outcome`} checked={x.outcome === o} onChange={() => onChange({ outcome: o })} /> {t(OUTCOME_LABEL[o])}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="field">
            <label htmlFor={`${id}-ev`}>{t('Evidence')}</label>
            <textarea id={`${id}-ev`} rows={3} maxLength={4000} value={x.evidence ?? ''} onChange={(e) => onChange({ evidence: e.target.value })} />
            <div className="toolbar">
              <button type="button" className="btn ghost" onClick={() => onChange({ evidence: `${auto.join('. ')}.` })}>{t('Use the numbers above')}</button>
            </div>
          </div>
          <div className="field">
            <label htmlFor={`${id}-bar`}>{t('What got in the way')}</label>
            <textarea id={`${id}-bar`} rows={2} maxLength={4000} value={x.barriers ?? ''} onChange={(e) => onChange({ barriers: e.target.value })} />
          </div>
          <fieldset className="planner-fieldset">
            <legend>{t('Is the support still right?')}</legend>
            <div className="planner-radios">
              <label><input type="radio" name={`${id}-ok`} checked={x.provisionOk === true} onChange={() => onChange({ provisionOk: true })} /> {t('Yes')}</label>
              <label><input type="radio" name={`${id}-ok`} checked={x.provisionOk === false} onChange={() => onChange({ provisionOk: false })} /> {t('No')}</label>
            </div>
          </fieldset>
          <div className="field">
            <label htmlFor={`${id}-next`}>{t('Next steps')}</label>
            <textarea id={`${id}-next`} rows={2} maxLength={4000} value={x.nextSteps ?? ''} onChange={(e) => onChange({ nextSteps: e.target.value })} />
          </div>
        </>
      )}
    </div>
  );
}

function PrintReview({ review: r, learnerName }: { review: Review; learnerName: string | null }) {
  const t = useT();
  const d = useDates();
  const stamp = useStamp();
  const text = (v: string | null) => v ?? t('Not recorded');
  return (
    <div className="planner-print planner-review-print">
      <h1>{r.title}</h1>
      {learnerName && <p>{t('Learner: {name}', { name: learnerName })}</p>}
      <p>{t('From {from} to {to}', { from: d.long(r.from), to: d.long(r.to) })}</p>
      {r.status === 'DRAFT' && <p><strong>{t('Draft. Not signed yet.')}</strong></p>}
      <h2>{t('Who took part')}</h2>
      <p>{text(r.attendees)}</p>
      <h2>{t("Parent's views")}</h2>
      <p>{text(r.parentViews)}</p>
      <h2>{t("Learner's views")}</h2>
      <p>{text(r.learnerViews)}</p>
      {r.summary && <><h2>{t('Overall summary')}</h2><p>{r.summary}</p></>}
      {groups(r.targets).map((g) => (
        <section key={g.goal} className="planner-review-print-goal">
          <h2>{t('Outcome: {goal}', { goal: g.goal })}</h2>
          <p>{t('Area of need: {area}', { area: t(AREA_LABEL[g.area]) })}</p>
          {g.targets.map((x) => (
            <div key={x.targetId} className="planner-review-print-target">
              <h3>{t('Target: {target}', { target: x.statement })}</h3>
              <dl>
                <dt>{t('Outcome')}</dt><dd>{x.outcome ? t(OUTCOME_LABEL[x.outcome]) : t('Not recorded')}</dd>
                <dt>{t('Evidence')}</dt><dd>{text(x.evidence)}</dd>
                <dt>{t('What got in the way')}</dt><dd>{text(x.barriers)}</dd>
                <dt>{t('Is the support still right?')}</dt><dd>{x.provisionOk == null ? t('Not recorded') : x.provisionOk ? t('Yes') : t('No')}</dd>
                <dt>{t('Next steps')}</dt><dd>{text(x.nextSteps)}</dd>
              </dl>
            </div>
          ))}
        </section>
      ))}
      <h2>{t('Signatures')}</h2>
      {r.signedParentName && r.signedParentAt && <p>{t('Signed by {name} on {date}', { name: r.signedParentName, date: stamp(r.signedParentAt) })}</p>}
      {r.signedConsultantName && r.signedConsultantAt && <p>{t('Signed by {name}, consultant, on {date}', { name: r.signedConsultantName, date: stamp(r.signedConsultantAt) })}</p>}
      {!r.signedParentAt && !r.signedConsultantAt && <p>{t('Not signed yet.')}</p>}
    </div>
  );
}
